import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X } from 'lucide-react';
import styles from './TimePicker.module.css';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const PERIODS = ['AM', 'PM'];

const pad = (n) => String(n).padStart(2, '0');

// 'HH:mm' (24h) -> { hour: 1-12, minute: 0-59, period }
const parse = (value) => {
  if (!value) return null;
  const [h, m] = value.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return { hour: h % 12 || 12, minute: m, period: h >= 12 ? 'PM' : 'AM' };
};

const toValue = ({ hour, minute, period }) => {
  const h24 = (hour % 12) + (period === 'PM' ? 12 : 0);
  return `${pad(h24)}:${pad(minute)}`;
};

const formatDisplay = (parts) => (parts ? `${pad(parts.hour)}:${pad(parts.minute)} ${parts.period}` : '');

const TimePicker = ({
  value = '',
  onChange,
  placeholder = 'Select time',
  disabled = false,
  className = '',
  size = 'default',
  dropdownPosition = 'bottom',
  minuteStep = 5,
  error = false,
}) => {
  const selected = parse(value);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);
  if (selected && !minutes.includes(selected.minute)) {
    minutes.push(selected.minute);
    minutes.sort((a, b) => a - b);
  }

  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Portaled to document.body like DatePicker, so it escapes scrollable modal bodies.
  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.getBoundingClientRect().height || 260;
    const fitsBelow = rect.bottom + dropdownHeight + 6 <= window.innerHeight;
    const placeAbove = dropdownPosition === 'top' || !fitsBelow;

    setDropdownStyle({
      position: 'fixed',
      top: placeAbove ? rect.top - dropdownHeight - 6 : rect.bottom + 6,
      left: rect.left,
      minWidth: Math.max(rect.width, 220),
    });
  }, [dropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const raf = requestAnimationFrame(() => {
      computePosition();
      dropdownRef.current?.querySelectorAll(`.${styles.optionSelected}`).forEach((el) => {
        el.parentElement.scrollTop = el.offsetTop - el.parentElement.clientHeight / 2 + el.clientHeight / 2;
      });
    });
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, computePosition]);

  const handleSelect = (field, v) => {
    const base = selected || { hour: 9, minute: 0, period: 'AM' };
    onChange(toValue({ ...base, [field]: v }));
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange('');
  };

  const renderColumn = (items, field, format) => (
    <div className={styles.column}>
      {items.map((item) => (
        <button
          key={item}
          type="button"
          className={`${styles.option} ${selected?.[field] === item ? styles.optionSelected : ''}`}
          onClick={() => handleSelect(field, item)}
        >
          {format(item)}
        </button>
      ))}
    </div>
  );

  return (
    <div ref={wrapperRef} className={`${styles.wrapper} ${className} ${disabled ? styles.disabled : ''}`}>
      <div
        ref={triggerRef}
        className={`${styles.trigger} ${size === 'compact' ? styles.triggerCompact : ''} ${open ? styles.triggerOpen : ''} ${error ? styles.triggerError : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !disabled && setOpen((o) => !o); } }}
      >
        <Clock className={styles.triggerIcon} size={size === 'compact' ? 14 : 16} />
        <span className={`${styles.triggerText} ${!selected ? styles.placeholder : ''}`}>
          {formatDisplay(selected) || placeholder}
        </span>
        {selected && !disabled && (
          <button type="button" className={styles.clearButton} onClick={handleClear} title="Clear">
            <X size={14} />
          </button>
        )}
      </div>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={dropdownStyle || { position: 'fixed', visibility: 'hidden' }}
        >
          <div className={styles.columns}>
            {renderColumn(HOURS, 'hour', pad)}
            {renderColumn(minutes, 'minute', pad)}
            {renderColumn(PERIODS, 'period', (p) => p)}
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.doneButton} onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TimePicker;
